import {CommonModule} from '@angular/common';
import {
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {
    BillingDetails,
    MerchantPlatformSettings,
    PaymentProvider,
    PaymentTokenData
} from '../../../core/types/payment.types';
import {IPaymentCardComponent, PaymentCardComponentConfig} from '../payment-card-component.interface';

@Component({
    selector: 'app-payment-card-input-wrapper',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="payment-card-wrapper">
            <ng-container #cardComponentContainer></ng-container>
        </div>
    `,
    styles: [`
        .payment-card-wrapper {
            width: 100%;
        }
    `]
})
export class PaymentCardInputWrapperComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('cardComponentContainer', { read: ViewContainerRef, static: true })
    cardComponentContainer!: ViewContainerRef;

    @Input() merchantPlatform!: MerchantPlatformSettings;
    @Input() showFormErrors: boolean = false;
    @Input() showCardHolderName: boolean = false;

    @Output() validationError = new EventEmitter<string>();
    @Output() ready = new EventEmitter<void>();

    private componentRef?: ComponentRef<any>;
    private cardComponent?: IPaymentCardComponent;
    private destroy$ = new Subject<void>();

    async ngOnInit(): Promise<void> {
        if (!this.merchantPlatform) {
            this.validationError.emit('merchantPlatform is required for payment-card-input-wrapper');
            return;
        }

        await this.loadProviderComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Update child component when showFormErrors changes
        if (changes['showFormErrors'] && !changes['showFormErrors'].firstChange && this.cardComponent) {
            (this.cardComponent as any).showFormErrors = this.showFormErrors;

            // Trigger change detection in the child component if it has a ChangeDetectorRef
            if (this.componentRef) {
                this.componentRef.changeDetectorRef.detectChanges();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    /**
     * Dynamically load the appropriate provider component based on platform
     */
    private async loadProviderComponent(): Promise<void> {
        this.cardComponentContainer.clear();

        try {
            let componentType: any;

            switch (this.merchantPlatform.platform) {
                case PaymentProvider.Stripe:
                    const stripeModule = await import('../providers/stripe-card-input/stripe-card-input.component');
                    componentType = stripeModule.StripeCardInputComponent;
                    break;

                case PaymentProvider.PayPal:
                    const paypalModule = await import('../providers/paypal-card-input/paypal-card-input.component');
                    componentType = paypalModule.PayPalCardInputComponent;
                    break;

                case PaymentProvider.AuthorizeNet:
                    const authorizeNetModule = await import('../providers/authorizenet-card-input/authorizenet-card-input.component');
                    componentType = authorizeNetModule.AuthorizeNetCardInputComponent;
                    break;

                default:
                    throw new Error(`Payment provider ${this.merchantPlatform.platform} is not supported`);
            }

            // Create the component
            this.componentRef = this.cardComponentContainer.createComponent(componentType);
            this.cardComponent = this.componentRef.instance as IPaymentCardComponent;

            this.cardComponent.validationError
                .pipe(takeUntil(this.destroy$))
                .subscribe(error => {
                    this.validationError.emit(error);
                });

            this.cardComponent.ready
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.ready.emit();
                });

            // Initialize the component with config
            const config: PaymentCardComponentConfig = {
                showCardHolderName: this.showCardHolderName,
                showFormErrors: this.showFormErrors
            };
            await this.cardComponent.initialize(this.merchantPlatform, config);

        } catch (error) {
            this.validationError.emit(`Failed to load payment provider: ${error}`);
        }
    }

    /**
     * Validate the card input
     * Called by parent component before submission
     */
    validate(): boolean {
        if (!this.cardComponent) {
            return false;
        }
        return this.cardComponent.validate();
    }

    /**
     * Get cardholder name from component if it manages it
     */
    getCardHolderName(): string | null {
        if (!this.cardComponent) {
            return null;
        }
        return this.cardComponent.getCardHolderName();
    }

    /**
     * Create payment method token
     * Called by parent component on form submission
     */
    async createPaymentMethod(billingDetails: BillingDetails): Promise<PaymentTokenData> {
        if (!this.cardComponent) {
            throw new Error('Payment component not initialized');
        }

        return await this.cardComponent.createPaymentMethod(billingDetails);
    }
}

